/**
 * Industry Projects Types
 * Polymorphic project/work tracking for project-based industries
 */

import { CompanyType } from '../company';

export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';

export interface IndustryProject {
  id: string;
  company_id: number;
  user_id: string;
  domain: CompanyType;

  // Common fields
  name: string;
  description?: string | null;
  client_id?: number | null;
  status: ProjectStatus;

  // Financial tracking
  budget_revenue?: number | null;
  budget_cost?: number | null;
  actual_cost: number;
  actual_revenue: number;

  // Dates
  start_date?: string | null;
  end_date?: string | null;
  completed_date?: string | null;

  // Industry-specific metadata
  metadata: Record<string, any>;

  // Audit
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Domain-Specific Metadata Types
// ============================================================================

export interface AgencyProjectMetadata {
  project_type?: 'branding' | 'campaign' | 'website' | 'app';
  team_members?: string[];
  deliverables?: Array<{
    name: string;
    deadline: string;
    status: string;
  }>;
}

export interface ConstructionProjectMetadata {
  construction_type?: 'renovation' | 'new_construction' | 'repair';
  property_address?: string;
  square_meters?: number;
  building_permit_number?: string;
  milestones?: Array<{
    name: string;
    percentage: number;
    amount: number;
    status: 'pending' | 'completed';
    due_date?: string;
  }>;
}

export interface ArchitectProjectMetadata {
  project_type?: 'architecture' | 'structural' | 'electrical' | 'plumbing';
  engineering_discipline?: string;
  regulatory_approvals?: string[];
  drawings_count?: number;
  stages?: Array<{
    name: string;
    status: string;
    due_date?: string;
  }>;
}

export interface ConsultingProjectMetadata {
  service_type?: string;
  deliverables?: Array<{
    name: string;
    deadline: string;
    status: string;
  }>;
}

export interface ITProjectMetadata {
  project_type?: 'development' | 'support' | 'infrastructure' | 'consulting';
  technology_stack?: string[];
  repository_url?: string;
  sla_level?: string;
}

export interface PhotographyProjectMetadata {
  event_type?: 'wedding' | 'corporate' | 'product' | 'portrait';
  event_date?: string;
  location?: string;
  photo_count_estimate?: number;
}

export interface EventProjectMetadata {
  event_type?: string;
  event_date?: string;
  location?: string;
  attendees_estimate?: number;
  vendors?: Array<{
    name: string;
    service: string;
    cost: number;
  }>;
}

// ============================================================================
// Type Guards
// ============================================================================

export function isAgencyProject(project: IndustryProject): project is IndustryProject & { metadata: AgencyProjectMetadata } {
  return project.domain === CompanyType.AGENCIES;
}

export function isConstructionProject(project: IndustryProject): project is IndustryProject & { metadata: ConstructionProjectMetadata } {
  return project.domain === CompanyType.CONSTRUCTION;
}

export function isArchitectProject(project: IndustryProject): project is IndustryProject & { metadata: ArchitectProjectMetadata } {
  return project.domain === CompanyType.ARCHITECTS_ENGINEERS;
}

export function isConsultingProject(project: IndustryProject): project is IndustryProject & { metadata: ConsultingProjectMetadata } {
  return project.domain === CompanyType.CONSULTING_B2B;
}

export function isITProject(project: IndustryProject): project is IndustryProject & { metadata: ITProjectMetadata} {
  return project.domain === CompanyType.IT_COMPANIES;
}

export function isPhotographyProject(project: IndustryProject): project is IndustryProject & { metadata: PhotographyProjectMetadata } {
  return project.domain === CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS;
}

export function isEventProject(project: IndustryProject): project is IndustryProject & { metadata: EventProjectMetadata } {
  return project.domain === CompanyType.EVENT_PRODUCERS;
}

// ============================================================================
// Input Types for Forms
// ============================================================================

export interface CreateProjectInput {
  name: string;
  description?: string;
  client_id?: number;
  status?: ProjectStatus;
  budget_revenue?: number;
  budget_cost?: number;
  start_date?: string;
  end_date?: string;
  metadata?: Record<string, any>;
}

export interface UpdateProjectInput extends Partial<CreateProjectInput> {
  id: string;
  completed_date?: string;
}
